import { Fragment, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useStore } from '@tanstack/react-form';
import {
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  Grid,
  Skeleton,
  Alert,
  Autocomplete,
} from '@mui/material';

import { useGetCategoriesQuery } from 'src/api/categories/getCategories';
import { useCreateGameMutation } from 'src/api/games/createGame';
import { useUpdateGameMutation } from 'src/api/games/updateGame';
import type { Game } from 'src/api/types';
import Iconify from 'src/components/iconify';
import { LocalizedDateTimePicker } from 'src/components/form/LocalizedDateTimePicker';
import { LanguageCodeFormSelect } from 'src/components/form/LanguageCodeFormSelect';
import { useGlobalFiltersStore } from 'src/store/globalFilters.store';
import { ItemsByCategory } from './ItemsByCategory';

interface Props {
  open: boolean;
  onClose: () => void;
  game?: Game;
}

export default function GameDialog({ onClose, ...props }: Props) {
  const { languageCode } = useGlobalFiltersStore();

  const [addCategory, setAddCategory] = useState(false);

  const form = useForm({
    defaultValues:
      props?.game ||
      ({
        title: '',
        categoryIds: [],
        languageCode,
        status: 'inactive',
        startTime: null,
        endTime: null,
      } as unknown as Game),
    onSubmit: async ({ value }) => {
      try {
        await saveGame(value);

        form.reset();
        onClose();
      } catch (error: any) {
        toast.error('Failed to save game:', error?.message);
      }
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        if (!value.languageCode) return toast.error('Sport is required');
        if (!value.title.trim()) return toast.error('Title is required');
        if (value.categoryIds.length < 4)
          return toast.error('Required to add 4 categories');
        return null;
      },
    },
  });

  const { categoryIds } = useStore(form.store, (state) => state.values);

  // API
  const { data: categories = [], isLoading: isLoadingCategories } =
    useGetCategoriesQuery();
  const { mutateAsync: createGame, isPending: isCreatePending } =
    useCreateGameMutation();
  const { mutateAsync: updateGame, isPending: isUpdatePending } =
    useUpdateGameMutation();

  const saveGame = async (game: Game) => {
    const body = { ...game };
    body.categoryIds = JSON.stringify(body.categoryIds) as any;

    if (body.id) {
      await updateGame(body);
      return;
    }

    await createGame(body);
  };

  const onCancel = () => {
    form.reset();
    onClose();
  };

  const handleDeleteCategory = (id: any) => {
    form.setFieldValue(
      'categoryIds',
      categoryIds.filter((item: any) => item !== id)
    );
  };

  const selectedSport = useStore(
    form.store,
    (state) => state.values.languageCode
  );

  return (
    <Dialog
      open={props.open}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='alert-dialog-title'>
        {form.state.values?.id ? `Edit ${form.state.values?.id}` : 'Create'}{' '}
        Game
      </DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <DialogContent>
          <Box sx={{ width: '100%' }}>
            <Grid sx={{ mb: 3, mt: 1 }}>
              <form.Field name='title'>
                {(field) => (
                  <TextField
                    id='title'
                    label='Title'
                    required
                    sx={{ width: '60%', mr: 2 }}
                    onChange={(e) => field.handleChange(e.target.value)}
                    value={field.state.value}
                  />
                )}
              </form.Field>

              <form.Field name='status'>
                {(field) => (
                  <FormControl sx={{ width: '35%' }} required>
                    <InputLabel id='type-select-label'>Status</InputLabel>
                    <Select
                      labelId='type-label'
                      id='status'
                      label='Status'
                      onChange={(e) => field.handleChange(e.target.value)}
                      value={field.state.value}
                    >
                      {['inactive', 'active', 'complete'].map((item) => (
                        <MenuItem key={item} value={item}>
                          {item.charAt(0).toUpperCase() + item.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </form.Field>
            </Grid>

            <Grid container gap={2}>
              <form.Field name='startTime'>
                {(field) => (
                  <LocalizedDateTimePicker
                    label='Start'
                    value={field.state.value}
                    onChange={field.handleChange}
                  />
                )}
              </form.Field>

              <form.Field name='endTime'>
                {(field) => (
                  <LocalizedDateTimePicker
                    label='End'
                    value={field.state.value}
                    onChange={field.handleChange}
                  />
                )}
              </form.Field>
            </Grid>

            <form.Field name='languageCode'>
              {(field) => (
                <LanguageCodeFormSelect
                  label='Sport'
                  required
                  defaultValue={languageCode}
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                    form.setFieldValue('categoryIds', []); // reset categories when language code changes
                  }}
                />
              )}
            </form.Field>

            {isLoadingCategories ? (
              <Skeleton width='100%' height={120} />
            ) : !Boolean(categories.length) ? (
              <Alert sx={{ mt: 3 }} severity='info'>
                No categories found for this sport
              </Alert>
            ) : (
              Boolean(categories.length) && (
                <>
                  <TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Difficulty</TableCell>
                          <TableCell>Items</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <form.Subscribe
                        selector={(state) => state.values.categoryIds}
                        children={(categoryIds) => (
                          <TableBody>
                            {categoryIds?.map((id: number) => {
                              const category = categories.find(
                                (item) => item.id === id
                              );
                              if (!category) return null;
                              return (
                                <Fragment key={id}>
                                  <TableRow
                                    sx={{
                                      '&:last-child td, &:last-child th': {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <TableCell component='th' scope='row'>
                                      {category.id}
                                    </TableCell>
                                    <TableCell component='th' scope='row'>
                                      {category.name}
                                    </TableCell>
                                    <TableCell component='th' scope='row'>
                                      {category.difficulty}
                                    </TableCell>
                                    <TableCell>
                                      <ItemsByCategory categoryId={id} />
                                    </TableCell>
                                    <TableCell align='right'>
                                      <Button
                                        variant='outlined'
                                        onClick={() =>
                                          handleDeleteCategory(category.id)
                                        }
                                        startIcon={
                                          <Iconify icon='eva:trash-2-outline' />
                                        }
                                      >
                                        Delete
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                </Fragment>
                              );
                            })}
                          </TableBody>
                        )}
                      ></form.Subscribe>
                    </Table>
                  </TableContainer>

                  {addCategory && categoryIds.length < 4 && (
                    <form.Field name='categoryIds'>
                      {(field) => (
                        <Autocomplete
                          sx={{ mt: 2 }}
                          options={categories.filter(
                            (cat) =>
                              !categoryIds.includes(cat.id) &&
                              cat.languageCode === selectedSport
                          )}
                          getOptionLabel={(option) =>
                            `${option.id} -  ${option.name} [${option.difficulty}]`
                          }
                          onChange={(_, newValue) => {
                            if (!newValue) return;
                            if (categoryIds.length >= 4) {
                              toast.error(
                                'You cannot create a new game that exceeds to 4 categories'
                              );
                              return;
                            }
                            field.handleChange([
                              ...field.state.value,
                              newValue.id,
                            ]);
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label='Category' />
                          )}
                        />
                      )}
                    </form.Field>
                  )}

                  {categoryIds.length < 4 && (
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant='contained'
                        startIcon={<Iconify icon='eva:plus-fill' />}
                        onClick={() => setAddCategory(true)}
                      >
                        Add Category
                      </Button>
                    </Box>
                  )}
                </>
              )
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type='submit'
            autoFocus
            disabled={isCreatePending || isUpdatePending}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
